/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {ArrowUturnLeftIcon, PaperAirplaneIcon, PencilIcon, XMarkIcon} from '@heroicons/react/24/outline'

import {useAppDispatch} from 'helpers/store'
import Dates from 'utils/Dates'
import {closeSecondaryModal, openSecondaryModal} from 'helpers/actions'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import {ModalSize} from 'types/enums/ModalSize'

import {OfferNote} from 'types/entities/OfferNote'
import {OffersAction} from 'store/offers/OffersAction'
import {toast} from 'utils/toastUtils'
import {useTypedSelector} from 'helpers/reducers'
import OfferBackOfficeNoteItem from './OfferBackOfficeNotesComponents/OfferBackOfficeNoteItem'
import OfferBackOfficeNotesList from './OfferBackOfficeNotesComponents/OfferBackOfficeNotesList'
import UiRichTextEditor from 'components/Ui/UiRichTextEditor'

interface OfferBackOfficeNotesProps {
    notes: OfferNote[]
    title?: string
    className?: string
    offerId: number
    disabled?: boolean
}

const OfferBackOfficeNotes = (props: OfferBackOfficeNotesProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [inputValue, setInputValue] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [replyingTo, setReplyingTo] = useState<OfferNote | null>(null)
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
    const scrollEndRef = useRef<HTMLDivElement>(null)

    const currentUser = useTypedSelector((state) => state.sessionState.session)
    const notes = useTypedSelector((state) => {
        const { selectedOffer, all } = state.offersState
        if (selectedOffer?.id === props.offerId) {
            return selectedOffer.offerNotes ?? []
        }

        const offerFromList = all.find((offer) => offer.id === props.offerId)
        return offerFromList?.offerNotes ?? props.notes
    })

    const scrollToBottom = () => {
        scrollEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [notes, replyingTo, editingNoteId])

    const onEditHandler = (note: OfferNote) => {
        setInputValue(note.note)
        setEditingNoteId(note.id)
        setReplyingTo(null)
        const input = document.getElementById('offer-note-input')
        if (input) input.focus()
    }

    const onDeleteHandler = (noteId: number) => {
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Delete Note')}
                    message={t('Are you sure you want to delete this note?')}
                    onConfirm={async () => {
                        try {
                            await dispatch(OffersAction.fetchDeleteOfferNote(props.offerId, noteId))
                            toast.success(t('Note deleted'))
                            dispatch(closeSecondaryModal())
                        } catch {
                            toast.error(t('Failed to delete note'))
                            dispatch(closeSecondaryModal())
                        }
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            onSubmitHandler()
        }
    }

    const onSubmitHandler = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        if (!inputValue.trim() || isLoading) return

        setIsLoading(true)
        try {
            if (editingNoteId) {
                await dispatch(OffersAction.fetchEditOfferNote(editingNoteId, inputValue))
                toast.success(t('Note updated'))
                setEditingNoteId(null)
            } else {
                await dispatch(OffersAction.fetchAddOfferNote(props.offerId, inputValue, replyingTo?.id))
                toast.success(t('Note added'))
            }
            setInputValue('')
            setReplyingTo(null)
        } catch {
            toast.error(t('Failed to save note'))
        } finally {
            setIsLoading(false)
        }
    }

    const getInitials = (name?: string) => {
        if (!name) return '?'
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    const getThreadedNotes = () => {
        const rootNotes: OfferNote[] = []
        const repliesMap = new Map<number, OfferNote[]>()

        const sortedNotes = [...notes].sort((a, b) => {
            const dateA = Dates.dayjs(a.created_at)
            const dateB = Dates.dayjs(b.created_at)
            return dateA.diff(dateB)
        })

        const noteIds = new Set(sortedNotes.map((note) => Number(note.id)))

        sortedNotes.forEach((note) => {
            const repliedNoteId =
                note.replied_note_id === null || note.replied_note_id === undefined
                    ? null
                    : Number(note.replied_note_id)

            if (repliedNoteId && noteIds.has(repliedNoteId)) {
                const list = repliesMap.get(repliedNoteId) || []
                list.push(note)
                repliesMap.set(repliedNoteId, list)
            } else {
                rootNotes.push(note)
            }
        })

        return { rootNotes, repliesMap }
    }

    const { rootNotes, repliesMap } = getThreadedNotes()
    const displayTitle = props.title ?? t('BackOffice Notes')
    const isDisabled = !!props.disabled

    const renderNote = (note: OfferNote, isReply = false): React.ReactNode => {
        return (
            <OfferBackOfficeNoteItem
                key={note.id}
                note={note}
                isReply={isReply}
                currentUser={currentUser}
                repliesMap={repliesMap}
                getInitials={getInitials}
                disabled={isDisabled}
                onReply={(n) => {
                    if (isDisabled) return
                    setReplyingTo(n)
                    setEditingNoteId(null)
                    setInputValue('')
                    setTimeout(() => {
                        const input = document.getElementById('offer-note-input')
                        if (input) input.focus()
                    }, 50)
                }}
                onEdit={(n) => {
                    if (isDisabled) return
                    onEditHandler(n)
                }}
                onDelete={(id) => {
                    if (isDisabled) return
                    onDeleteHandler(id)
                }}
                renderNote={renderNote}
            />
        )
    }

    return (
        <div className={clsx('OfferBackOfficeNotes UiCard p-2 flex flex-col h-full', props.className)}>
            <div className="flex items-center justify-between mb-2 pb-1 border-b border-gray-200 dark:border-gray-700 shrink-0">
                <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">{displayTitle}</h3>
            </div>

            <div className="flex-1 overflow-y-auto min-h-[130px] max-h-[500px] mb-2 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                <div className="flex flex-col gap-1 p-1">
                    {rootNotes.length === 0 && (
                        <div className="text-center text-gray-400 text-xs py-4 italic">
                            {t('No notes yet. Type a message to start.')}
                        </div>
                    )}
                    <OfferBackOfficeNotesList notes={rootNotes} renderNote={renderNote} />
                    <div ref={scrollEndRef} />
                </div>
            </div>

            {!isDisabled && (
            <div className="flex flex-col gap-2 shrink-0 border-t border-gray-100 dark:border-gray-800 pt-2">
                {replyingTo && (
                    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 px-2 py-1.5 rounded-md text-xs border border-blue-100 dark:border-blue-900/30">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <ArrowUturnLeftIcon className="size-3 text-blue-500" />
                            <span className="text-gray-500 dark:text-gray-400 truncate">
                                {t('Replying to')}{' '}
                                <span className="font-semibold text-blue-600 dark:text-blue-400">
                                    {replyingTo.creator?.name || t('Unknown')}
                                </span>
                                : "{replyingTo.note.substring(0, 30)}
                                {replyingTo.note.length > 30 ? '...' : ''}"
                            </span>
                        </div>
                        <button
                            onClick={() => setReplyingTo(null)}
                            className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <XMarkIcon className="size-3" />
                        </button>
                    </div>
                )}

                {editingNoteId && (
                    <div className="flex items-center justify-between bg-orange-50 dark:bg-orange-900/20 px-2 py-1.5 rounded-md text-xs border border-orange-100 dark:border-orange-900/30">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <PencilIcon className="size-3 text-orange-500" />
                            <span className="text-gray-500 dark:text-gray-400 truncate">{t('Editing note')}</span>
                        </div>
                        <button
                            onClick={() => {
                                setEditingNoteId(null)
                                setInputValue('')
                            }}
                            className="p-0.5 hover:bg-orange-100 dark:hover:bg-orange-800 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <XMarkIcon className="size-3" />
                        </button>
                    </div>
                )}

                <form onSubmit={onSubmitHandler} onKeyDown={handleEditorKeyDown} className="flex flex-col gap-2">
                    <div className="OfferBackOfficeNotesEditor flex-flex-col items-stretch">
                        <UiRichTextEditor
                            value={inputValue}
                            onChange={setInputValue}
                            placeholder={
                                replyingTo ? (t('Type your response...') as string) : (t('Type a note...') as string)
                            }
                            className="min-h-[200px] border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden"
                        />
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">Ctrl+Enter {t('Send')}</span>
                        <button
                            type="submit"
                            disabled={isLoading || !inputValue.trim()}
                            className={clsx(
                                'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed',
                                editingNoteId ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-500 hover:bg-blue-600',
                                isLoading && 'opacity-50 cursor-not-allowed'
                            )}
                            title={editingNoteId ? t('Update') : t('Send')}
                        >
                            <PaperAirplaneIcon className="w-4 h-4" />
                            {editingNoteId ? t('Update') : t('Send')}
                        </button>
                    </div>
                </form>
            </div>
            )}
        </div>
    )
}

export default OfferBackOfficeNotes
